/**
 * Admin → Industry Packs
 *
 * Phase 6 (locked 2026-05-15). Tab switcher inside a single page:
 *   Tab 1 - Tenant Defaults: gallery of 14 anchor packs. Customer
 *           activates/deactivates per anchor. These are the defaults
 *           for ALL BUs unless a BU has its own override.
 *   Tab 2 - Per-BU Overrides: matrix of BUs × packs. When a BU has
 *           any explicit override, those win at classifier-resolution
 *           time; otherwise the BU inherits tenant defaults.
 *
 * Multi-industry tenants (the customer-style) use Tab 2 to map specific
 * BUs to specific industry anchors (e.g. Wealth Mgmt → BFSI Capital
 * Markets, Insurance Distribution → BFSI Insurance).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Sparkles,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Building2,
  Globe,
  ShieldCheck,
  Layers,
  Activity,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listIndustryPacks,
  activateIndustryPack,
  deactivateIndustryPack,
  listBusinessUnits,
  listBuIndustryPacks,
  activateBuIndustryPack,
  deactivateBuIndustryPack,
  type BusinessUnit,
  type IndustryPackAnchor,
  type IndustryPackSummary,
} from '@/lib/api';

type Tab = 'tenant' | 'per-bu';

export function AdminIndustryPacks(): JSX.Element {
  const [packs, setPacks] = useState<IndustryPackSummary[]>([]);
  const [bus, setBus] = useState<BusinessUnit[]>([]);
  const [tab, setTab] = useState<Tab>('tenant');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [packsResult, busResult] = await Promise.all([
        listIndustryPacks(),
        listBusinessUnits().catch(() => ({ businessUnits: [] })),
      ]);
      setPacks(packsResult.packs);
      setBus(busResult.businessUnits.filter((b) => b.status === 'active' && b.source !== 'system'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load industry packs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleTenantToggle(pack: IndustryPackSummary): Promise<void> {
    setBusy(pack.anchor);
    setError(null);
    try {
      if (pack.tenantActivation.activated) {
        await deactivateIndustryPack(pack.anchor);
      } else {
        await activateIndustryPack(pack.anchor);
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to toggle activation');
    } finally {
      setBusy(null);
    }
  }

  const totals = useMemo(() => {
    const active = packs.filter((p) => p.tenantActivation.activated).length;
    const importedFromCore = packs.filter(
      (p) => p.tenantActivation.source === 'foctta_core',
    ).length;
    const totalPerBuActivations = packs.reduce((sum, p) => sum + p.perBuActiveCount, 0);
    return { active, importedFromCore, totalPerBuActivations };
  }, [packs]);

  return (
    <PageContainer>
      <PageHeader
        title="Admin · Industry Packs"
        subtitle="Activate industry-specific classifier overlays + ROPA templates + toxic combinations. Default for all BUs lives on the Tenant Defaults tab; per-BU overrides on the Per-BU Overrides tab. Locked decision: hybrid activation model (2026-05-15)."
        badges={[
          { label: `${totals.active}/${packs.length} active`, variant: 'success' },
          { label: `${totals.importedFromCore} from FOCTTA core`, variant: 'info' },
          { label: `${totals.totalPerBuActivations} per-BU overrides`, variant: 'glass' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      <div className="flex items-center gap-2 mb-4">
        <TabButton active={tab === 'tenant'} onClick={() => setTab('tenant')}>
          <Globe className="h-3.5 w-3.5" />
          Tenant Defaults
        </TabButton>
        <TabButton active={tab === 'per-bu'} onClick={() => setTab('per-bu')}>
          <Building2 className="h-3.5 w-3.5" />
          Per-BU Overrides
          {bus.length > 0 && (
            <span className="text-[10px] text-muted-foreground ml-1">({bus.length} BUs)</span>
          )}
        </TabButton>
      </div>

      {loading ? (
        <GlassCard className="p-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> loading industry packs…
        </GlassCard>
      ) : tab === 'tenant' ? (
        <TenantDefaultsGallery packs={packs} busy={busy} onToggle={handleTenantToggle} />
      ) : (
        <PerBuMatrix
          bus={bus}
          packs={packs}
          onChange={refresh}
          onError={(msg) => setError(msg)}
        />
      )}
    </PageContainer>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? 'text-xs px-3 py-1.5 rounded-md bg-primary/15 text-foreground border border-primary/40 transition-colors flex items-center gap-1.5'
          : 'text-xs px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors flex items-center gap-1.5'
      }
    >
      {children}
    </button>
  );
}

// ── Tab 1 - Tenant defaults gallery ──────────────────────────────────

function TenantDefaultsGallery({
  packs,
  busy,
  onToggle,
}: {
  packs: IndustryPackSummary[];
  busy: string | null;
  onToggle: (p: IndustryPackSummary) => Promise<void>;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {packs.map((pack, i) => (
        <motion.div
          key={pack.anchor}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.02, 0.3) }}
        >
          <PackCard pack={pack} busy={busy === pack.anchor} onToggle={() => onToggle(pack)} />
        </motion.div>
      ))}
    </div>
  );
}

function PackCard({
  pack,
  busy,
  onToggle,
}: {
  pack: IndustryPackSummary;
  busy: boolean;
  onToggle: () => void;
}): JSX.Element {
  const active = pack.tenantActivation.activated;
  const fromCore = pack.tenantActivation.source === 'foctta_core';
  return (
    <GlassCard
      className={
        active ? 'p-4 border-success/40 glow-low h-full flex flex-col' : 'p-4 h-full flex flex-col'
      }
    >
      <CardHeader className="p-0 mb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-1.5">
            <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
            {pack.name}
          </span>
          {active && (
            <Badge variant="success" className="text-[10px]">
              <CheckCircle2 className="h-3 w-3 mr-0.5" />
              Active
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-[10px] font-mono">{pack.anchor}</CardDescription>
      </CardHeader>

      <div className="flex flex-wrap gap-1 mb-2">
        {pack.geographies.map((g) => (
          <Badge key={g} variant="glass" className="text-[9px]">
            {g}
          </Badge>
        ))}
        {fromCore && (
          <Badge variant="info" className="text-[9px]">
            <ShieldCheck className="h-3 w-3 mr-0.5" />
            FOCTTA core
          </Badge>
        )}
        {pack.perBuActiveCount > 0 && (
          <Badge variant="warning" className="text-[9px]">
            <Building2 className="h-3 w-3 mr-0.5" />
            {pack.perBuActiveCount} per-BU
          </Badge>
        )}
      </div>

      {pack.regulatoryClauses.length > 0 && (
        <div className="text-[10px] text-muted-foreground mb-2">
          <span className="text-muted-foreground">Regulators: </span>
          {pack.regulatoryClauses.slice(0, 5).join(' · ')}
          {pack.regulatoryClauses.length > 5 && (
            <span> +{pack.regulatoryClauses.length - 5}</span>
          )}
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-[10px] text-muted-foreground">
          {active && pack.tenantActivation.activatedAt
            ? `since ${new Date(pack.tenantActivation.activatedAt).toLocaleDateString()}`
            : 'not active for tenant'}
        </span>
        <Button
          size="sm"
          variant={active ? 'glass' : 'default'}
          onClick={onToggle}
          disabled={busy}
          data-action="disc.adm_packs.a01 disc.adm_packs.a02"
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : active ? (
            'Deactivate'
          ) : (
            <>
              <Sparkles className="h-3 w-3" />
              Activate
            </>
          )}
        </Button>
      </div>
    </GlassCard>
  );
}

// ── Tab 2 - Per-BU overrides matrix ─────────────────────────────────

function PerBuMatrix({
  bus,
  packs,
  onChange,
  onError,
}: {
  bus: BusinessUnit[];
  packs: IndustryPackSummary[];
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}): JSX.Element {
  if (bus.length === 0) {
    return (
      <GlassCard className="p-10 text-center text-muted-foreground">
        <Building2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No business units yet.</p>
        <p className="text-[11px] mt-1">
          Create business units under Settings & Admin → Business Units, then come back to assign
          industry packs per-BU.
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-0 overflow-hidden">
      <div className="p-4 border-b border-border/40">
        <p className="text-xs text-muted-foreground">
          Per-BU overrides take precedence over tenant defaults at classifier-resolution time. When
          a BU has at least one explicit override row here, ONLY the row(s) marked active for that
          BU are used; tenant defaults are NOT merged in. Leave a BU empty to fall back to the
          Tenant Defaults tab.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left p-3 sticky left-0 bg-muted/30 z-10">Business Unit</th>
              {packs.map((p) => (
                <th
                  key={p.anchor}
                  className="text-center p-2 min-w-[110px] border-l border-border/30"
                  title={p.name}
                >
                  <div className="font-medium truncate max-w-[100px]">{p.name}</div>
                  <div className="text-[9px] text-muted-foreground font-mono mt-0.5">
                    {p.anchor.split('_').slice(0, 2).join('_')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bus.map((bu) => (
              <BuMatrixRow
                key={bu.id}
                bu={bu}
                packs={packs}
                onChange={onChange}
                onError={onError}
              />
            ))}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}

function BuMatrixRow({
  bu,
  packs,
  onChange,
  onError,
}: {
  bu: BusinessUnit;
  packs: IndustryPackSummary[];
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}): JSX.Element {
  const [activations, setActivations] = useState<Record<string, boolean>>({});
  const [resolutionMode, setResolutionMode] = useState<'override' | 'inherited' | 'tenant_default'>(
    'tenant_default',
  );
  const [effective, setEffective] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listBuIndustryPacks(bu.id);
      const map: Record<string, boolean> = {};
      for (const a of result.activations) {
        if (a.activated) map[a.packId] = true;
      }
      setActivations(map);
      setResolutionMode(result.resolutionMode);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'failed to load BU activations');
    } finally {
      setLoading(false);
    }
  }, [bu.id, onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggle(anchor: IndustryPackAnchor): Promise<void> {
    setBusy(anchor);
    try {
      if (activations[anchor]) {
        await deactivateBuIndustryPack(bu.id, anchor);
      } else {
        await activateBuIndustryPack(bu.id, anchor);
      }
      await refresh();
      await onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'failed to toggle BU pack');
    } finally {
      setBusy(null);
    }
  }

  return (
    <tr className="border-t border-border/30 hover:bg-muted/10">
      <td className="p-3 sticky left-0 bg-card z-10 border-r border-border/30">
        <div className="flex items-center gap-2 min-w-[180px]">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{bu.name}</div>
            <div className="text-[10px] text-muted-foreground font-mono truncate">{bu.slug}</div>
          </div>
          {resolutionMode === 'override' ? (
            <Badge variant="warning" className="text-[9px] shrink-0">
              override
            </Badge>
          ) : resolutionMode === 'inherited' ? (
            <Badge variant="glass" className="text-[9px] shrink-0" title="Packs come from a parent business unit">
              inherits: parent
            </Badge>
          ) : (
            <Badge variant="glass" className="text-[9px] shrink-0" title="No packs on this unit or any parent - falling back to the tenant default">
              inherits: tenant
            </Badge>
          )}
        </div>
      </td>
      {packs.map((p) => {
        const isActive = !!activations[p.anchor];
        const isBusyHere = busy === p.anchor;
        // Read the RESOLVED set rather than inferring from the tenant row:
        // with parent inheritance a pack can be in force via an ancestor, which
        // the tenant activation says nothing about.
        const inheritsActive = !isActive && effective.has(p.anchor);
        return (
          <td
            key={p.anchor}
            className="p-2 text-center border-l border-border/30"
            title={
              isActive
                ? `Active for ${bu.name} (override)`
                : inheritsActive
                  ? `Inherited from tenant default`
                  : 'Inactive for this BU'
            }
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin mx-auto opacity-50" />
            ) : (
              <button
                onClick={() => toggle(p.anchor)}
                disabled={isBusyHere}
                data-action="disc.adm_packs.a03 disc.adm_packs.a04"
                className={
                  isActive
                    ? 'h-7 w-7 rounded-md bg-success/20 border border-success/40 text-success hover:bg-success/30 transition-colors mx-auto flex items-center justify-center'
                    : inheritsActive
                      ? 'h-7 w-7 rounded-md bg-muted/30 border border-border/40 text-muted-foreground hover:bg-muted/50 transition-colors mx-auto flex items-center justify-center'
                      : 'h-7 w-7 rounded-md border border-border/30 text-muted-foreground/40 hover:bg-muted/40 hover:text-foreground transition-colors mx-auto flex items-center justify-center'
                }
              >
                {isBusyHere ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : isActive ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : inheritsActive ? (
                  <Activity className="h-3 w-3" />
                ) : (
                  <span className="text-[10px]">+</span>
                )}
              </button>
            )}
          </td>
        );
      })}
    </tr>
  );
}
